import React from 'react';
import { Settings } from 'lucide-react';
import { SearchableSelect } from '@/components/SearchableSelect';
import { SearchableSelectLazy } from '@/components/SearchableSelectLazy';
import { US_STATES } from '@/data/usLocations';
import AccountCodeField from '@/components/calculator/AccountCodeFieldEnhanced';

export default function ClientInfoBlock({
  scenarioInfo,
  setScenarioInfo,
  services,
  handleServiceChange,
  billingFrequencies,
  accountExecutives,
  readerTypes,
  readerTypesWithCosts,
  processors,
  user,
  setShowServiceManager,
  onProcessorChange,  // Callback for when processors change
  onAccountCodeChange  // NEW: Callback for when account code changes
}) {
  return (
    <div className="card-modern p-0 overflow-hidden">
      <div className="bg-gradient-to-r from-primary to-indigo-700 px-6 py-4 relative">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjAzIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
        <h2 className="text-lg font-bold text-white relative z-10">
          BLOCK 1: SCENARIO INFORMATION <span className="text-accent text-sm ml-2">(All fields required *)</span>
        </h2>
      </div>
      <div className="p-6 grid grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Municipality <span className="text-red-600">*</span>
            <span className="ml-2 text-xs text-slate-500">(Searchable or type manually)</span>
          </label>
          <div className="relative">
            <SearchableSelectLazy
              apiEndpoint="/locations"
              value={scenarioInfo.municipality}
              onChange={(value) => setScenarioInfo({ ...scenarioInfo, municipality: value })}
              placeholder="Type to search city or county..."
              searchPlaceholder="Search municipalities..."
              minSearchLength={2}
              allowManualEntry={true}
            />
            <input
              type="text"
              value={scenarioInfo.municipality}
              onChange={(e) => setScenarioInfo({ ...scenarioInfo, municipality: e.target.value })}
              onFocus={(e) => setTimeout(() => e.target.select(), 0)}
              onClick={(e) => e.target.select()}
              className="input-field bg-yellow-50 mt-2"
              placeholder="Or type municipality name manually..."
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">State <span className="text-red-600">*</span></label>
          <select
            value={scenarioInfo.state}
            onChange={(e) => setScenarioInfo({ ...scenarioInfo, state: e.target.value })}
            className="input-field bg-yellow-50"
          >
            <option value="">Select state...</option>
            {US_STATES.map(state => (
              <option key={state.code} value={state.code}>{state.code} - {state.name}</option>
            ))}
          </select>
        </div>
        
        {/* Account Code Field - Auto-populated based on Municipality with Subdimension selection */}
        <div className="col-span-2">
          <AccountCodeField
            municipalityName={scenarioInfo.municipality}
            selectedServiceType={scenarioInfo.service}
            selectedState={scenarioInfo.state}
            value={scenarioInfo.accountCode || ''}
            onChange={(code, details) => {
              setScenarioInfo(prev => ({ 
                ...prev, 
                accountCode: code,
                accountCodeDetails: details  // Store full details for reference
              }));
              if (onAccountCodeChange) onAccountCodeChange(code, details);
            }}
            disabled={false}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Service / Product <span className="text-red-600">*</span></label>
          <SearchableSelect
            options={services}
            value={scenarioInfo.service}
            onChange={handleServiceChange}
            placeholder="Select service..."
            searchPlaceholder="Search services..."
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Billing Frequency <span className="text-red-600">*</span></label>
          <select
            value={scenarioInfo.billingFrequency}
            onChange={(e) => setScenarioInfo({ ...scenarioInfo, billingFrequency: e.target.value })}
            className="input-field bg-yellow-50"
          >
            {billingFrequencies.map(freq => (
              <option key={freq} value={freq}>{freq}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Scenario Name <span className="text-red-600">*</span></label>
          <input
            type="text"
            value={scenarioInfo.scenarioName}
            onChange={(e) => setScenarioInfo({ ...scenarioInfo, scenarioName: e.target.value })}
            className="input-field bg-yellow-50"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">As-Of Date <span className="text-red-600">*</span></label>
          <input
            type="date"
            value={scenarioInfo.asOfDate}
            onChange={(e) => setScenarioInfo({ ...scenarioInfo, asOfDate: e.target.value })}
            className="input-field bg-yellow-50"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Account Executive <span className="text-red-600">*</span></label>
          <select
            value={scenarioInfo.accountExecutive}
            onChange={(e) => setScenarioInfo({ ...scenarioInfo, accountExecutive: e.target.value })}
            className="input-field bg-yellow-50"
          >
            <option value="">Select account executive...</option>
            {accountExecutives.map(exec => (
              <option key={exec.id} value={exec.name}>{exec.name}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Readers Qty <span className="text-red-600">*</span></label>
          <input
            type="number"
            min="0"
            value={scenarioInfo.readersQty || 0}
            onChange={(e) => {
              const value = e.target.value;
              // Allow empty string for clearing, otherwise parse as number
              const numValue = value === '' ? 0 : Math.max(0, parseInt(value, 10) || 0);
              setScenarioInfo({ ...scenarioInfo, readersQty: numValue });
            }}
            onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
            className="input-field bg-yellow-50"
            placeholder="Enter 0 or more"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Reader Type <span className="text-red-600">*</span></label>
          <select
            value={scenarioInfo.readerType}
            onChange={(e) => setScenarioInfo({ ...scenarioInfo, readerType: e.target.value })}
            className="input-field bg-yellow-50"
          >
            <option value="">-- Select Reader Type --</option>
            {readerTypes.length === 0 && scenarioInfo.readerType && (
              <option key={scenarioInfo.readerType} value={scenarioInfo.readerType}>
                {scenarioInfo.readerType}
              </option>
            )}
            {readerTypes.map(type => {
              // Find cost from readerTypesWithCosts if available
              const readerData = (readerTypesWithCosts || []).find(rt => rt.name === type);
              const monthlyCost = readerData?.monthly_cost || 0;
              const displayText = monthlyCost > 0 ? `${type} - $${monthlyCost}/month` : type;
              
              return (
                <option key={type} value={type}>{displayText}</option>
              );
            })}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Partnership (Y/N) <span className="text-red-600">*</span></label>
          <select
            value={scenarioInfo.partnership}
            onChange={(e) => setScenarioInfo({ ...scenarioInfo, partnership: e.target.value })}
            className="input-field bg-yellow-50"
          >
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
        
        {/* Partnership Commission Rate - Only shown when Partnership = Yes */}
        {scenarioInfo.partnership === 'Yes' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Partnership Commission % <span className="text-red-600">*</span>
              <span className="ml-2 text-xs text-slate-500">(Applied to card net revenue)</span>
            </label>
            <div className="relative">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={scenarioInfo.partnershipCommissionRate || 25}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  setScenarioInfo({ ...scenarioInfo, partnershipCommissionRate: Math.min(100, Math.max(0, value)) });
                }}
                className="input-field bg-yellow-50 pr-10"
                placeholder="25"
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 text-sm font-medium">
                %
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Default: 25% | Staff commissions remain at 5% (with partnership) or 10% (no partnership)</p>
          </div>
        )}
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Card Processor <span className="text-red-600">*</span></label>
          <select
            value={scenarioInfo.cardProcessor}
            onChange={(e) => {
              setScenarioInfo({ ...scenarioInfo, cardProcessor: e.target.value });
              if (onProcessorChange) onProcessorChange('card', e.target.value);
            }}
            className="input-field bg-yellow-50"
          >
            <option value="">Select Card Processor</option>
            {processors.filter(p => p.processor_type === 'Card Processor').map(proc => (
              <option key={proc.id} value={proc.name}>{proc.name}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">ACH Processor <span className="text-red-600">*</span></label>
          <select
            value={scenarioInfo.achProcessor}
            onChange={(e) => {
              setScenarioInfo({ ...scenarioInfo, achProcessor: e.target.value });
              if (onProcessorChange) onProcessorChange('ach', e.target.value);
            }}
            className="input-field bg-yellow-50"
          >
            <option value="">Select ACH Processor</option>
            {processors.filter(p => p.processor_type === 'ACH Processor').map(proc => (
              <option key={proc.id} value={proc.name}>{proc.name}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
